import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Title = styled.h1`
    color: white;
    text-align: center;
    margin-bottom: 40px;
    font-size: 3rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const NavigationGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 30px;
    width: 100%;
    max-width: 1000px;
`;

const PageCard = styled.div`
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 30px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
    }
`;

const PageTitle = styled.h2`
    color: #333;
    margin-bottom: 15px;
    font-size: 1.5rem;
    border-bottom: 2px solid #667eea;
    padding-bottom: 10px;
`;

const PageDescription = styled.p`
    color: #666;
    margin-bottom: 20px;
    line-height: 1.6;
    font-size: 1rem;
`;

const PageLink = styled(Link)`
    display: inline-block;
    padding: 12px 24px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    text-decoration: none;
    border-radius: 25px;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

    &:hover {
        background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        transform: translateY(-2px);
    }
`;

const FeaturesList = styled.ul`
    color: #555;
    margin: 15px 0;
    padding-left: 20px;
    line-height: 1.5;
`;

const Home: React.FC = () => (
    <Container>
        <Title>Welcome to Japan Study Frontend</Title>

        <NavigationGrid>
            <PageCard>
                <PageTitle>🏠 Home</PageTitle>
                <PageDescription>
                    You&apos;re currently on the home page! This is your central hub for navigating
                    to all the different sections of the application. Use this page to quickly
                    access any feature.
                </PageDescription>
                <FeaturesList>
                    <li>Overview of all available features</li>
                    <li>Quick navigation to any section</li>
                    <li>Detailed descriptions of each page</li>
                    <li>Beautiful, responsive design</li>
                </FeaturesList>
                <PageLink to="/">You&apos;re Here!</PageLink>
            </PageCard>

            <PageCard>
                <PageTitle>⚛️ Learn React</PageTitle>
                <PageDescription>
                    Interactive React learning tutorials and examples. Explore React concepts,
                    components, and modern development practices through hands-on examples.
                </PageDescription>
                <FeaturesList>
                    <li>React fundamentals and concepts</li>
                    <li>Component-based architecture</li>
                    <li>Modern React features and hooks</li>
                    <li>Interactive code examples</li>
                </FeaturesList>
                <PageLink to="/learn-react">Explore React</PageLink>
            </PageCard>

            <PageCard>
                <PageTitle>🇯🇵 Study Japanese</PageTitle>
                <PageDescription>
                    A comprehensive Japanese language learning platform with vocabulary management
                    and study sessions. Perfect for building your Japanese vocabulary and testing
                    your knowledge.
                </PageDescription>
                <FeaturesList>
                    <li>Add new vocabulary with English translations</li>
                    <li>Interactive study sessions with randomized questions</li>
                    <li>View and manage your vocabulary list</li>
                    <li>Real-time input validation</li>
                </FeaturesList>
                <PageLink to="/study-japanese">Start Learning Japanese</PageLink>
            </PageCard>

            <PageCard>
                <PageTitle>🎨 Component Showcase</PageTitle>
                <PageDescription>
                    Discover and interact with all the custom components built for this application.
                    Test different animations, styles, and see the component library in action.
                </PageDescription>
                <FeaturesList>
                    <li>Motion Liquid Buttons with 6 animation variants</li>
                    <li>CSS-based interactive components</li>
                    <li>Input field demonstrations</li>
                    <li>Card component examples</li>
                    <li>Animation performance details</li>
                </FeaturesList>
                <PageLink to="/showcase">View Components</PageLink>
            </PageCard>
        </NavigationGrid>
    </Container>
);

export default Home;
